import { produce, type Draft } from "immer";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as config from "../config";
import { RequestContext } from "../request-context";
import { outputDate } from "../utils/date";
import { requestGet, requireResult } from "../utils/network";
import ButtonsGroup from "../widget/buttons-group";
import { Modes, type ModesList } from "../widget/io-abstract";
import IODate from "../widget/io-date";
import Panel from "../widget/panel";
import Restricted from "../widget/restricted";
import {
  patientFilesList,
  type Appointment,
  type Bill,
  type ConsultClubfoot,
  type ConsultOther,
  type ConsultRicket,
  type Patient,
  type PatientFileType,
  type PatientFull,
  type Picture,
  type Pojo,
  type Surgery
} from "./_objects-patient";
import AppointmentElement from "./appointment-element";
import BillElement from "./bill-element";
import ConsultClubfootElement from "./consult-clubfoot-element";
import ConsultOtherElement from "./consult-other-element";
import ConsultRicketElement from "./consult-ricket-element";
import PatientElement from "./patient-element";
import PictureElement from "./picture-element";
import SurgeryElement from "./surgery-element";

// Exported for testing
export function getNextAppointment(patient: PatientFull): string | undefined {
  const today = new Date();
  return Object.values(patient.appointment ?? {})
    .map((appointment) => appointment.date)
    .filter((d) => d != undefined)
    .map((d) => new Date(d))
    .filter((d) => d > today)
    .sort((a, b) => b.getTime() - a.getTime()) // Bigger at top
    .map(outputDate)
    .shift();
}

// Exported for testing
export function getLastSeen(patient: PatientFull): string | undefined {
  // All but appointment. Appointment is only an intend.
  const today = new Date();
  return [
    ...Object.values(patient.bill ?? {}),
    ...Object.values(patient.consult_clubfoot ?? {}),
    ...Object.values(patient.consult_other ?? {}),
    ...Object.values(patient.consult_ricket ?? {}),
    ...Object.values(patient.picture ?? {}),
    ...Object.values(patient.surgery ?? {})
  ]
    .map((v) => "date" in v && v.date)
    .filter((d) => d)
    .map((d) => new Date(d as string))
    .filter((d) => d < today)
    .sort((a, b) => a.getTime() - b.getTime())
    .map(outputDate)
    .pop();
}

export default function PagePatient(): React.ReactNode {
  //
  // React context
  //

  const params = useParams();
  const navigate = useNavigate();
  const requestContext = useContext(RequestContext)!;
  const [patient, setPatient] = useState<PatientFull | undefined>(undefined);

  const props: {
    id: string;
    selectedType?: PatientFileType;
    selectedId?: string;
    mode: ModesList;
  } = {
    id: params["id"]!,
    selectedType: params["selectedType"] as PatientFileType,
    selectedId: params["selectedId"],
    mode: params["mode"] == "edit" ? Modes.input : Modes.output
  };

  //
  // Patient
  //

  if (!props.id) {
    return <div key="no-patient-selected">No Patient id selected</div>;
  }

  useEffect(() => {
    setPatient(undefined);
    requestContext
      .fetchJSONAndMessage<PatientFull>(
        `Loading patient #${props.id}`,
        requestGet(`/api/patient/${props.id}`)
      )
      .then(requireResult)
      .then((patient) => setPatient(patient))
      .catch(() => {});
  }, [props.id]);

  if (!patient) {
    return <div key="no-patient-selected">No Patient selected</div>;
  }

  //
  // --- From here, we have a patient! ---
  //

  //
  // Handle the add mode
  //

  {
    // Remove dandling add files
    const onlyIncludedTypes = patientFilesList.filter(
      // Add mode? exclude selectedType
      (t) => props.selectedId != "add" || t !== props.selectedType
    );
    if (
      onlyIncludedTypes
        .map((type) => Object.values(patient[type] ?? {}).filter((f) => !f.id))
        .reduce((acc, l) => acc + l.length, 0) > 0
    ) {
      // Remove dandling file and update State
      setPatient(
        produce(patient, (draft) => {
          onlyIncludedTypes.forEach((type) => {
            const map = draft[type];
            if (map) {
              Object.keys(map).forEach((key) => {
                if (!map[key]?.id || key === "add") {
                  delete map[key];
                }
              });
            }
          });
        })
      );
    }

    // Add a stub if necessary
    if (props.selectedId == "add") {
      props.mode = Modes.input;
      const currentList = Object.values(patient[props.selectedType!] ?? {});
      if (currentList.filter((f) => !f.id).length == 0) {
        setPatient(
          produce(patient, (draft) => {
            if (!draft[props.selectedType!]) {
              draft[props.selectedType!] = {};
            }
            draft[props.selectedType!]!["add"] = {
              patient_id: patient.id
            } as Draft<
              | Appointment
              | Bill
              | ConsultClubfoot
              | ConsultOther
              | ConsultRicket
              | Picture
              | Surgery
            >;
          })
        );
      }
    }
  }

  //
  // Let's go
  //

  const selectedUid = props.selectedType
    ? `${props.selectedType}.${props.selectedId ?? "add"}`
    : `patient.${props.selectedId}`;

  const buildCommonProps = <T extends Pojo>(
    type: PatientFileType,
    file: T,
    getMap: (patient: Patient) => Record<string, T> | undefined
  ) => {
    const uid = `${type}.${file.id ?? "add"}`;
    const edit = uid == selectedUid ? props.mode === Modes.input : false;
    return {
      hidden: props.mode === Modes.input && !edit,
      patient: patient!,
      closed: uid !== selectedUid,
      parentPath: `/patient/${patient!.id}`,
      edit,
      onCreated: (subFile: T) =>
        setPatient(
          produce(patient!, (draft) => {
            if (!getMap(draft)) {
              draft[type] = {};
            }
            delete getMap(draft)!["add"];
            getMap(draft)![subFile.id!] = subFile;
          })
        ),
      onUpdated: (subFile: T) =>
        setPatient(
          produce(patient!, (draft) => {
            if (!getMap(draft)) {
              draft[type] = {};
            }
            getMap(draft)![subFile.id!] = subFile;
          })
        ),
      onDeleted: (subFile: T) =>
        setPatient(
          produce(patient!, (draft) => {
            if (draft[type]) {
              delete getMap(draft)![subFile.id!];
              delete getMap(draft)!["add"];
            }
          })
        )
    };
  };

  const calculateOrder = (file: Pojo) => {
    if ("date" in file && file.date) {
      if (file.date instanceof Date) {
        return -file.date.getTime() / (1000 * 3600 * 24);
      }
      return -new Date(file.date as string).getTime() / (1000 * 3600 * 24);
    }
    return 0;
  };

  return (
    <div
      key="top-folder"
      data-testid={"folder-" + patient.id}
      className="reduce-width"
    >
      {/* ------------ Header  --------------------*/}
      {props.mode === Modes.output && (
        <>
          <Restricted to="write">
            <ButtonsGroup>
              <button
                id="btnAddSelector"
                type="button"
                className="action-alternate btn btn-secondary dropdown-toggle"
                data-testid="add"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Add
              </button>
              <div
                className="dropdown-menu dropdown-menu-end text-end"
                aria-labelledby="btnGroupDrop1"
              >
                {patientFilesList.map((type) => (
                  <Link
                    className="dropdown-item"
                    key={type}
                    data-testid={`add-${type}`}
                    to={`/patient/${patient.id!}/${type}/add`}
                  >
                    {config.type2Title(type)}
                  </Link>
                ))}
              </div>
            </ButtonsGroup>
          </Restricted>
          <Panel key="key-dates" label="Key dates">
            <IODate label="Last seen" value={getLastSeen(patient)} />
            <IODate
              label="Next appointment"
              value={getNextAppointment(patient)}
            />
          </Panel>
        </>
      )}
      <PatientElement
        key={`patient/${patient.id ?? "add"}`}
        hidden={
          props.mode === Modes.input && `patient.${patient.id}` !== selectedUid
        }
        file={patient}
        closed={`patient.${patient.id}` !== selectedUid}
        isProtected={
          patientFilesList
            .map((f) => Object.values(patient[f] ?? {}).length)
            .reduce((a, b) => a + b, 0) > 0
        }
        edit={
          `patient.${patient.id}` == selectedUid
            ? props.mode === Modes.input
            : false
        }
        onUpdated={(newPatient: Patient) =>
          setPatient({
            ...newPatient,
            appointment: patient.appointment,
            bill: patient.bill,
            consult_clubfoot: patient.consult_clubfoot,
            consult_other: patient.consult_other,
            consult_ricket: patient.consult_ricket,
            picture: patient.picture,
            surgery: patient.surgery
          })
        }
        onDeleted={() => navigate("/")}
      />
      <div style={{ display: "flex", flexDirection: "column" }}>
        {Object.values(patient.appointment ?? {}).map((file: Appointment) => (
          <div
            key={`appointment/${file.id ?? "add"}`}
            style={{ order: calculateOrder(file) }}
          >
            <AppointmentElement
              key={`appointment/${file.id ?? "add"}`}
              file={file as Appointment}
              {...buildCommonProps<Appointment>(
                "appointment",
                file,
                (patient) => patient.appointment
              )}
            />
          </div>
        ))}
        {Object.values(patient.bill ?? {}).map((file: Bill) => (
          <div
            key={`bill/${file.id ?? "add"}`}
            style={{ order: calculateOrder(file) }}
          >
            <BillElement
              key={`bill/${file.id ?? "add"}`}
              file={file}
              {...buildCommonProps<Bill>(
                "bill",
                file,
                (patient) => patient.bill
              )}
            />
          </div>
        ))}
        {Object.values(patient.consult_clubfoot ?? {}).map(
          (file: ConsultClubfoot) => (
            <div
              key={`consult_clubfoot/${file.id ?? "add"}`}
              style={{ order: calculateOrder(file) }}
            >
              <ConsultClubfootElement
                key={`consult_clubfoot/${file.id ?? "add"}`}
                file={file}
                {...buildCommonProps<ConsultClubfoot>(
                  "consult_clubfoot",
                  file,
                  (patient) => patient.consult_clubfoot
                )}
              />
            </div>
          )
        )}
        {Object.values(patient.consult_other ?? {}).map(
          (file: ConsultOther) => (
            <div
              key={`consult_other/${file.id ?? "add"}`}
              style={{ order: calculateOrder(file) }}
            >
              <ConsultOtherElement
                key={`consult_other/${file.id ?? "add"}`}
                file={file}
                {...buildCommonProps<ConsultOther>(
                  "consult_other",
                  file,
                  (patient) => patient.consult_other
                )}
              />
            </div>
          )
        )}
        {Object.values(patient.consult_ricket ?? {}).map(
          (file: ConsultRicket) => (
            <div
              key={`consult_ricket/${file.id ?? "add"}`}
              style={{ order: calculateOrder(file) }}
            >
              <ConsultRicketElement
                key={`consult_ricket/${file.id ?? "add"}`}
                file={file}
                {...buildCommonProps<ConsultRicket>(
                  "consult_ricket",
                  file,
                  (patient) => patient.consult_ricket
                )}
              />
            </div>
          )
        )}
        {Object.values(patient.picture ?? {}).map((file: Picture) => (
          <div
            key={`picture/${file.id ?? "add"}`}
            style={{ order: calculateOrder(file) }}
          >
            <PictureElement
              key={`picture/${file.id ?? "add"}`}
              file={file}
              {...buildCommonProps<Picture>(
                "picture",
                file,
                (patient) => patient.picture
              )}
            />
          </div>
        ))}
        {Object.values(patient.surgery ?? {}).map((file: Surgery) => (
          <div
            key={`surgery/${file.id ?? "add"}`}
            style={{ order: calculateOrder(file) }}
          >
            <SurgeryElement
              key={`surgery/${file.id ?? "add"}`}
              file={file}
              {...buildCommonProps<Surgery>(
                "surgery",
                file,
                (patient) => patient.surgery
              )}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
